import { keyframes } from '@emotion/react';
import type { CSSProperties, FC, ReactNode } from 'react';
import FoundationIcon from 'src/components-v2/DataDisplay/Icon/FoundationIcon';
import Link from 'src/components-v2/Inputs/Link/Link';
import { useCriticalRenderContext } from 'src/hooks/useCriticalRenderContext';
import styled from 'threads5/styles/styled';

const HoverTransition: CSSProperties['transition'] = '.25s ease';

const StyledCardSurface = styled('a')(({ theme }) => {
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: '16px',
    color: 'inherit',
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover, :focus-visible': {
      '.cta-circle': {
        backgroundColor: theme.palette['base/sky/900'],
        borderColor: 'transparent',
        color: '#F1FAFF',
      },
      '.border-image': {
        visibility: 'visible',
        opacity: 1,
        animationPlayState: 'running',
      },
      '.border-padding': {
        inset: '4px',
        borderRadius: '12px',
      },
      '.border-padding-countersink': {
        inset: '-4px',
      },
    },
    ':focus-visible': {
      outlineOffset: '6px',
    },
    [theme.breakpoints.up('lg')]: {
      borderRadius: '24px',
      ':hover, :focus-visible': {
        '.border-padding': {
          borderRadius: '20px',
        },
      },
    },
  };
});

const StyledBorderImageAnimation = keyframes`
  to { rotate: 360deg; }
`;

const StyledBorderImage = styled('img')({
  position: 'absolute',
  maxWidth: 'none',
  minWidth: '100%',
  minHeight: '100%',
  aspectRatio: 1,
  scale: 1.5,
  zIndex: 0,
  left: '50%',
  top: '50%',
  animation: `${StyledBorderImageAnimation} 10s linear infinite paused`,
  translate: '-50% -50%',
  transition: HoverTransition,
  opacity: 0,
  visibility: 'hidden',
  transitionProperty: 'opacity, visibility',
});

const StyledBorderPadding = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  borderRadius: 'inherit',
  transition: HoverTransition,
  transitionProperty: 'inset, border-radius',
  overflow: 'hidden',
});

const StyledBorderPaddingCountersink = styled('div')(({ theme }) => {
  return {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    transition: HoverTransition,
    transitionProperty: 'inset',
  };
});

const StyledBackgroundImage = styled('div')(({ theme }) => {
  return {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    // Note: we don't use the theme.palette['gradient/background/stark/elizabeth'] here because it's historically
    // reversed, and would look jarring when real images load in with the proper gradient direction.
    background: `linear-gradient(46deg, ${theme.palette['gradient/background/stark/elizabeth-start']} 2.73%, ${theme.palette['gradient/background/stark/elizabeth-mid']} 47.11%, ${theme.palette['gradient/background/stark/elizabeth-end']} 100%)`,
    img: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  };
});

const StyledForeground = styled('div')(({ theme }) => {
  return {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
  };
});

const StyledForegroundImage = styled('div')({
  display: 'flex',
  minHeight: 0,
  picture: {
    width: '100%',
    img: {
      objectFit: 'cover',
      objectPosition: 'top center',
      height: '100%',
      width: '100%',
      transition: HoverTransition,
      transitionProperty: 'translate, scale',
    },
  },
});

const StyledHeader = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    padding: '24px 19px 0 16px',
    [theme.breakpoints.up('md')]: {
      padding: '24px 22px 0 16px',
    },
    [theme.breakpoints.up('lg')]: {
      gap: theme.spacing(3),
      padding: '29px 27px 0 24px',
    },
  };
});

const StyledCopyText = styled('h3', {
  shouldForwardProp: (prop) => {
    return prop !== 'isPlaidSansLoaded';
  },
})<{ isPlaidSansLoaded: boolean }>(({ theme, isPlaidSansLoaded }) => {
  return {
    margin: 0,
    fontFamily: theme.typography.plaidSans,
    color: '#5C7695',
    fontSize: '2rem',
    letterSpacing: '-0.027rem',
    fontWeight: 500,
    lineHeight: '140%',
    maxWidth: '460px',
    textWrap: 'balance',
    transition: 'opacity .3s ease-in-out',
    ...(!isPlaidSansLoaded && {
      opacity: 0,
    }),
    [theme.breakpoints.up('lg')]: {
      fontSize: '2.6rem',
      letterSpacing: '-0.05rem',
    },
    [theme.breakpoints.up('xxl')]: {
      fontSize: '2.9rem',
    },
  };
});

const StyledPrimaryCopy = styled('span')(({ theme }) => {
  return {
    color: theme.palette['base/sky/1000'],
    fontWeight: 600,
  };
});

const StyledCtaCircle = styled('span')(({ theme }) => {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    flexShrink: 0,
    border: `1px solid ${theme.palette['base/purple/400']}`,
    color: '#02284B',
    transition: HoverTransition,
    transitionProperty: 'background-color, color, border-color',
  };
});

const SolutionsBentoCard: FC<{
  backgroundImage: ReactNode;
  foregroundImage: ReactNode;
  foregroundImageStyles?: CSSProperties;
  foregroundImageHoverStyles?: CSSProperties;
  primaryCopy: ReactNode;
  secondaryCopy?: ReactNode;
  href: string;
  lgHeight: number;
}> = ({
  backgroundImage,
  foregroundImage,
  foregroundImageStyles,
  foregroundImageHoverStyles,
  primaryCopy,
  secondaryCopy,
  href,
  lgHeight,
}) => {
  const { isPlaidSansLoaded } = useCriticalRenderContext();

  return (
    <Link href={href}>
      <StyledCardSurface
        sx={{
          '.foreground-image picture img': foregroundImageStyles,
          ':hover, :focus-visible': {
            '.foreground-image picture img': foregroundImageHoverStyles,
          },
          height: { xs: 417, lg: lgHeight },
        }}
      >
        <StyledBorderImage
          className='border-image'
          alt=''
          src='https://images.ctfassets.net/ss5kfr270og3/4EB5WsXthy4beE37T83K9R/328d3843044e1b8a87aefd97b8eb478f/homepage-solutions-border-image.png?fm=webp&q=70&w=400'
          loading='lazy'
        />

        <StyledBorderPadding className='border-padding'>
          <StyledBorderPaddingCountersink className='border-padding-countersink'>
            <StyledBackgroundImage>{backgroundImage}</StyledBackgroundImage>

            <StyledForeground>
              <StyledHeader>
                <StyledCopyText isPlaidSansLoaded={isPlaidSansLoaded}>
                  <StyledPrimaryCopy>{primaryCopy}</StyledPrimaryCopy>{' '}
                  {secondaryCopy}
                </StyledCopyText>

                <StyledCtaCircle className='cta-circle'>
                  <FoundationIcon icon='ArrowRight' size={14} />
                </StyledCtaCircle>
              </StyledHeader>

              <StyledForegroundImage className='foreground-image'>
                {foregroundImage}
              </StyledForegroundImage>
            </StyledForeground>
          </StyledBorderPaddingCountersink>
        </StyledBorderPadding>
      </StyledCardSurface>
    </Link>
  );
};

export default SolutionsBentoCard;
